import React, { useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFileExcel, faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useDownloadImportTemplateMutation, useImportUsersFromExcelMutation } from '../../../services/portal';
import { convertFileToBase64, generateTemplateFilename } from '../../../utils/fileUtils';
import FileUploadZone from './FileUploadZone';
import ImportOptions from './ImportOptions';
import ImportProgress from './ImportProgress';

/**
 * UserImport component for importing users from an Excel file.
 * @param {function} onImportComplete - Callback when import completes successfully.
 * @param {function} onClose - Callback to close the import dialog.
 */
const UserImport = ({ onImportComplete, onClose }) => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [sendEmails, setSendEmails] = useState(true);

  const [importUsers, { isLoading: uploading }] = useImportUsersFromExcelMutation();
  const [downloadTemplate] = useDownloadImportTemplateMutation();

  /**
   * Handles file selection from the upload zone.
   * @param {File} selectedFile - The selected file object.
   */
  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
  };

  /**
   * Downloads the Excel template for user import.
   * Shows a success or error toast based on a result. Create blob URL and download
   */
  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadTemplate().unwrap();

      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;

      link.download = generateTemplateFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Failed to download template: ' + (error.data?.message || error.message));
    }
  };

  /**
   * Uploads the selected Excel file and starts the import process.
   * Convert file to Base64. Handle different result scenarios with safe property access
   * Shows progress and handles results/errors.
   */
  const handleUploadFile = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setProgress(0);

    try {
      const fileData = await convertFileToBase64(file);

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const result = await importUsers({
        fileData,
        fileName: file.name,
        sendEmails
      }).unwrap();

      clearInterval(progressInterval);
      setProgress(100);

      // Trigger the import complete callback with results
      if (onImportComplete) {
        onImportComplete(result);
      }

      const dbResults = result.results?.database;
      const validationResults = result.results?.validation;
      const successfulCount = dbResults?.successful?.length || 0;
      const errorCount = validationResults?.errors?.length || 0;
      const skippedCount = validationResults?.skipped?.length || 0;

      if (successfulCount > 0) {
        toast.success(`Successfully imported ${successfulCount} users`);
      } else if (errorCount > 0 || skippedCount > 0) {
        toast.warning(result.message || 'Import completed with validation issues');
      } else {
        toast.info(result.message || 'Import completed');
      }

    } catch (error) {
      console.error('Import error:', error);

      clearInterval(progressInterval);
      setProgress(0);

      let errorMessage = 'Import failed';
      if (error.data?.message) {
        errorMessage += ': ' + error.data.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }

      if (error.data?.results && onImportComplete) {
        onImportComplete(error.data);
      }

      toast.error(errorMessage);
    } finally {
      setTimeout(() => setProgress(0), 2000);
    }
  };


  /**
   * Resets the form to the initial state for a new import.
   */
  const resetForm = () => {
    setFile(null);
  };


  return (
    <>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faFileExcel} className="mr-2 text-success" />
              Import Users from Excel
            </h5>
            <Button
              variant="link"
              size="sm"
              onClick={onClose}
              className="text-gray p-1 border-0"
              style={{ fontSize: '1.2rem' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        </Card.Header>

        <Card.Body>
          <div className="mb-4">
            <h6>Step 1: Download Template</h6>
            <p className="text-muted">
              Download the Excel template with the correct format and sample data.
            </p>
            <div className="text-center">
              <Button
                variant="primary"
                onClick={handleDownloadTemplate}
                className="mb-3"
              >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Download Excel Template
              </Button>
            </div>
          </div>

          <FileUploadZone
            file={file}
            onFileSelect={handleFileSelect}
            onReset={resetForm}
          />

          <ImportOptions
            sendEmails={sendEmails}
            onSendEmailsChange={setSendEmails}
          />

          <ImportProgress
            progress={progress}
            isVisible={uploading}
          />

          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleUploadFile}
              disabled={!file || uploading}
              className="px-4"
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} className="mr-2" />
                  Import Users
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

    </>
  );
};

export default UserImport;
